import BaseStore from 'customer-flows/stores/base-store'
import { context } from 'customer-flows/utils/core'
import {
  dateLevelHasSpotsFor,
  dateLevelMaxFor,
  hasSlotLevelAnswer
} from 'customer-flows/availability/tickets'

export default class RentalTicketsStoreClass extends BaseStore {
  @context('availability:validTimeslots') validTimeslots: AvailabilityStore['validTimeslots']
  @context('availability:isAvailableStatus') isAvailableStatus: AvailabilityStore['isAvailableStatus']
  @context('availability:availabilityDates') availabilityDates: AvailabilityStore['availabilityDates']
  @context('availability:selectedDate') selectedDate: AvailabilityStore['selectedDate']
  @context('availability:timesLoading') timesLoading: AvailabilityStore['timesLoading']

  // Which tier can answer — see `availability/tickets.ts` for the ladder.
  private get slotLevel(): boolean {
    return hasSlotLevelAnswer({ selectedDate: this.selectedDate, timesLoading: this.timesLoading })
  }

  /**
   * Highest per-type qty across the selected date's matching slots, or `undefined` when no slot
   * carries an availability entry for this ticket at all — which is NOT the same as a qty of 0 and
   * is why this returns a nullable rather than defaulting to 0. Callers distinguish them.
   *
   * A quantity-scoped response cannot OVERWRITE a sibling type's entry — the fold unions per
   * `resourceOptionId` and a kayak-scoped response simply carries no paddleboard entry (DEV-9267
   * cannot bite through this path). What it can do is fail to CARRY one: pre-ticket, the scoped
   * projection's slots come from the all-type dates seed and hold every type, but once quantities
   * go non-zero `pickDatesEntry` prefers the quantity-scoped week entry outright, so an unselected
   * sibling stops appearing in `validTimeslots` and this returns `undefined` for it — callers then
   * fall back to the date-level tier. This used to be documented as being covered by folding an
   * all-type TIMES seed under the selected-date entry; that family returned zero results from the
   * BE unconditionally and has been removed, so the coverage it described never existed.
   */
  private getMaxQtyFromSlots(ticketId: string): number | undefined {
    let max: number | undefined

    this.validTimeslots.forEach((slot) => {
      const ticketAvailability = slot.availability.find(({ resourceOptionId }) => resourceOptionId === ticketId)
      if (ticketAvailability) {
        max = Math.max(max ?? 0, ticketAvailability.qty)
      }
    })

    return max
  }

  isTicketAvailable(ticketId: string): boolean {
    if (!this.slotLevel) return dateLevelHasSpotsFor(this.availabilityDates.value, ticketId)

    // Per-type qty, not slot status: rental equipment types have independent inventory, so a slot's
    // status can read NOT_AVAILABLE purely because a SIBLING type is exhausted. Status is only
    // consulted when no slot carries a per-type entry for this ticket at all.
    const max = this.getMaxQtyFromSlots(ticketId)
    if (max !== undefined) return max > 0

    return this.validTimeslots.some((slot) => this.isAvailableStatus(slot.status))
  }

  getMaxAvailableQtyForTicket(ticketId: string): number {
    if (!this.slotLevel) return dateLevelMaxFor(this.availabilityDates.value, ticketId)

    // No spotsMax cap: each rental type has its own inventory, so a slot's shared `spots` ceiling
    // does not bound a single type the way it does for activities.
    return this.getMaxQtyFromSlots(ticketId) ?? 0
  }
}

declare global {
  export type RentalTicketsStore = RentalTicketsStoreClass
  export interface StoreRegistry {
    'tickets/rental': RentalTicketsStore
  }
}
